/* Post-kiosk donation journey hook — Phase 4 §4.1 split (GC-P1-05). */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedName } from 'pi-kiosk-shared';
import { useNavigate } from 'react-router-dom';
import { useDonationProjectByCode } from '../../donation-projects/donationProjectsApi.js';
import { usePostKioskResolution } from '../../checkout/PostKioskResolutionContext.js';
import { usePaymentStatus } from '../../checkout/checkoutApi.js';
import { FIO_PAYMENT_STATUS_POLL_MS } from '../../checkout/paymentPollPolicy.js';
import { isSignedInNavChrome } from '../../hub/customerHubPaths.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { useCustomerAnalytics } from '../../../shared/analytics/CustomerAnalyticsProvider.js';
import { useGiftSurfaceVisibility } from '../../navigation/useGiftSurfaceVisibility.js';
import { resolveShopHref } from '../../shop/resolveShopHref.js';
import { postKioskLog } from '../logging.js';

const MAX_PENDING_WINDOW_MS = 10 * 60_000;

export type PostKioskDonationRenderStatus = 'PENDING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';

export function usePostKioskDonationJourney(): {
  readonly t: ReturnType<typeof useTranslation>['t'];
  readonly paymentId: string;
  readonly donationCode: string | null;
  readonly projectName: string | undefined;
  readonly projectDescription: string | null | undefined;
  readonly renderStatus: PostKioskDonationRenderStatus;
  readonly title: string;
  readonly subtitle: string;
  readonly onPostPayContinue: () => void;
  readonly onRetry: () => void;
  readonly onRefresh: () => void;
} {
  const { t, i18n } = useTranslation('customer');
  const navigate = useNavigate();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const resolution = usePostKioskResolution();
  const paymentId = resolution?.paymentId ?? '';
  const donationCode = resolution?.donationProjectCode ?? null;
  const project = useDonationProjectByCode(donationCode);
  const status = usePaymentStatus(paymentId, FIO_PAYMENT_STATUS_POLL_MS, {
    enabled: paymentId.length > 0});
  const analytics = useCustomerAnalytics();
  const gift = useGiftSurfaceVisibility();

  useEffect(() => {
    analytics.track({
      eventName: 'screen_viewed',
      flow: 'DONATION',
      screenName: 'post_kiosk_donation',
      paymentId: paymentId.length > 0 ? paymentId : null,
      metadata: { donationProjectCode: donationCode }});
  }, [analytics, paymentId, donationCode]);

  useEffect(() => {
    if (!status.isError) {
      return;
    }
    postKioskLog.error('Post-kiosk donation payment status load failed', status.error, {
      operation: 'paymentStatus',
      tenantCode,
      hasPaymentId: paymentId.length > 0});
  }, [paymentId.length, status.error, status.isError, tenantCode]);

  useEffect(() => {
    if (!project.isError) {
      return;
    }
    postKioskLog.error('Post-kiosk donation project load failed', project.error, {
      operation: 'donationProject',
      tenantCode,
      hasDonationCode: donationCode !== null});
  }, [donationCode, project.error, project.isError, tenantCode]);

  const onPostPayContinue = useCallback((): void => {
    // G12/U1 / G5: URL-tenant account/donations only when !R1 && session after /me.
    // Under R1: no URL-B account deep-link — guest-safe donate catalog / L20 shopHref.
    const shopHref = resolveShopHref(undefined, session);
    const signedInOnUrlTenant = isSignedInNavChrome({ session });
    if (signedInOnUrlTenant) {
      if (gift.myDonationsVisible) {
        navigate('/account/donations', { replace: true });
        return;
      }
      navigate(shopHref, { replace: true });
      return;
    }
    if (gift.donateVisible) {
      navigate(`/${tenantCode}/donate`, { replace: true });
      return;
    }
    navigate(shopHref, { replace: true });
  }, [
    session,
    gift.donateVisible,
    gift.myDonationsVisible,
    navigate,
    tenantCode,
  ]);

  const startedAtRef = useRef<number | null>(null);
  const [escalatedExpired, setEscalatedExpired] = useState(false);
  const liveStatus = status.data?.status ?? 'PENDING';

  useEffect(() => {
    if (liveStatus !== 'FAILED') {
      return;
    }
    postKioskLog.warn('Post-kiosk donation payment failed', {
      operation: 'donationPayment',
      tenantCode,
      hasPaymentId: paymentId.length > 0});
  }, [liveStatus, paymentId.length, tenantCode]);

  useEffect(() => {
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }
    if (liveStatus !== 'PENDING' || escalatedExpired) {
      return;
    }
    const elapsed = Date.now() - startedAtRef.current;
    const remaining = MAX_PENDING_WINDOW_MS - elapsed;
    if (remaining <= 0) {
      setEscalatedExpired(true);
      return;
    }
    const handle = window.setTimeout(() => {
      setEscalatedExpired(true);
    }, remaining);
    return (): void => {
      window.clearTimeout(handle);
    };
  }, [liveStatus, escalatedExpired]);

  const renderStatus = useMemo<PostKioskDonationRenderStatus>(() => {
    if (liveStatus === 'COMPLETED') {
      return 'COMPLETED';
    }
    if (liveStatus === 'FAILED') {
      return 'FAILED';
    }
    if (liveStatus === 'EXPIRED' || escalatedExpired) {
      return 'EXPIRED';
    }
    return 'PENDING';
  }, [liveStatus, escalatedExpired]);

  const title = (() => {
    if (renderStatus === 'COMPLETED') {
      return t('postKioskDonation.thanks');
    }
    if (renderStatus === 'FAILED') {
      return t('postKioskDonation.failed');
    }
    if (renderStatus === 'EXPIRED') {
      return t('postKioskDonation.expired');
    }
    return t('postKioskDonation.processing');
  })();

  const projectDisplayName =
    project.data !== undefined
      ? resolveLocalizedName(project.data.name, project.data.nameLocales, i18n.language)
      : undefined;
  const subtitle = projectDisplayName ?? t('postKioskDonation.donationFallback');

  const onRetry = useCallback((): void => {
    window.location.reload();
  }, []);

  const onRefresh = useCallback((): void => {
    window.location.reload();
  }, []);

  return {
    t,
    paymentId,
    donationCode,
    projectName: projectDisplayName,
    projectDescription: project.data?.description,
    renderStatus,
    title,
    subtitle,
    onPostPayContinue,
    onRetry,
    onRefresh};
}
